import type { ActionFormationDTO } from '@/features/api/extranet/dto/Demande/AF'
import { TypesContextesAutorisesEnum, type TypeContextCourant } from '@/features/auth/context-app'
import { PropertyStatus, type PropertyStatusConfiguration } from '@/features/domaine/property-status'
import { idccAutoriseTT } from '@/features/domaine/regles-akto/shared'
import { TypeQualificationNomenclatures } from '@/features/domaine/types/opco/Demande/AF'
import { isNullOrUndefined } from '@/features/shared/tools'
import type { ContextReglesAf } from './types'

function selonTypeContextCourant(
  typeContext: TypeContextCourant,
  conditionVrai: PropertyStatus,
  conditionFaux: PropertyStatus,
) {
  return function (_value: any, _parent: any, _modele: ActionFormationDTO, context: ContextReglesAf) {
    if (typeContext === TypesContextesAutorisesEnum.Adherent) {
      return context.typeContext === TypesContextesAutorisesEnum.Adherent ? conditionVrai : conditionFaux
    } else {
      return context.typeContext === TypesContextesAutorisesEnum.Prestataire ? conditionVrai : conditionFaux
    }
  }
}

function selonTypeContextCourantArray(
  typeContext: TypeContextCourant,
  conditionVrai: PropertyStatus,
  conditionFaux: PropertyStatus,
) {
  return function (_value: any, _index: any, _parent: any, _modele: ActionFormationDTO, context: ContextReglesAf) {
    if (typeContext === TypesContextesAutorisesEnum.Adherent) {
      return context.typeContext === TypesContextesAutorisesEnum.Adherent ? conditionVrai : conditionFaux
    } else {
      return context.typeContext === TypesContextesAutorisesEnum.Prestataire ? conditionVrai : conditionFaux
    }
  }
}

const optionnelPrestataireArray = selonTypeContextCourantArray(
  TypesContextesAutorisesEnum.Adherent,
  PropertyStatus.Required,
  PropertyStatus.Optional,
)

const optionnelPrestataire = selonTypeContextCourant(
  TypesContextesAutorisesEnum.Adherent,
  PropertyStatus.Required,
  PropertyStatus.Optional,
)
/*
const requiredOrNotPresentPrestataire = selonTypeContextCourant(
  TypesContextesAutorisesEnum.Adherent,
  PropertyStatus.Required,
  PropertyStatus.NotPresent,
)
*/

function estTT(model: ActionFormationDTO) {
  return idccAutoriseTT.includes(model.employeur?.etablissement?.idcc?.code ?? '')
}

export const propertyStatusConfiguration: PropertyStatusConfiguration<ActionFormationDTO, ContextReglesAf> = {
  sousDispositif: PropertyStatus.Required,
  referenceProjet: (_value, _parent, model) => {
    if (idccAutoriseTT.includes(model.employeur?.etablissement?.idcc?.code ?? '')) {
      return PropertyStatus.Optional
    }
    return PropertyStatus.NotPresent
  },
  lieuSignature: optionnelPrestataire,
  attestationPiecesJustificatives: optionnelPrestataire,
  attestationSalariesSaisies: (_value, _parent, model, context) =>
    context.typeContext === TypesContextesAutorisesEnum.Prestataire && (model.salaries?.length ?? 0) > 0
      ? PropertyStatus.Required
      : PropertyStatus.NotPresent,
  attestationCadreInterventionFse: (_value, _parent, model) =>
    model.sousDispositif === 'Fse' ? PropertyStatus.Required : PropertyStatus.NotPresent,
  attestationEngagementsFse: (_value, _parent, model) =>
    model.sousDispositif === 'Fse' ? PropertyStatus.Required : PropertyStatus.NotPresent,
  attestationInformationParticipantsFse: (_value, _parent, model, context) =>
    model.sousDispositif === 'Fse' && context.typeContext === TypesContextesAutorisesEnum.Adherent
      ? PropertyStatus.Required
      : PropertyStatus.NotPresent,
  employeur: {
    contact: {
      email: PropertyStatus.Required,
    },
    tailleEntreprise: (_value, _parent, model, context) => {
      if (
        ['Fse', 'PicIae', 'Fne'].includes(model.sousDispositif!) &&
        context.typeContext === TypesContextesAutorisesEnum.Adherent
      )
        return PropertyStatus.Required

      return PropertyStatus.NotPresent
    },
  },
  salaries: {
    nomNaissance: PropertyStatus.Required,
    prenom: PropertyStatus.Required,
    sexe: PropertyStatus.Required,
    dateNaissance: optionnelPrestataireArray,
    nirTemporaire: (_value, _index, _parent, model, context) => {
      if (context.typeContext === TypesContextesAutorisesEnum.Prestataire) return PropertyStatus.Optional
      if (model.sousDispositif === 'AfFpe') return PropertyStatus.Optional

      return PropertyStatus.Required
    },
    nir: (_value, _index, _parent, model, context) => {
      if (context.typeContext === TypesContextesAutorisesEnum.Prestataire) return PropertyStatus.Optional
      if (model.sousDispositif === 'AfFpe') return PropertyStatus.Optional
      return PropertyStatus.Required
    },
    beneficieReconnaissanceTravailleurHandicape: optionnelPrestataireArray,
    adresse: {
      voie: optionnelPrestataire,
      codePostal: optionnelPrestataire,
      commune: optionnelPrestataire,
    },
    natureContrat: optionnelPrestataireArray,
    csp: optionnelPrestataireArray,
    statut: (_value, _index, _parent, _model, context) => {
      if (context.typeContext === TypesContextesAutorisesEnum.Prestataire) return PropertyStatus.Optional

      return PropertyStatus.Required
    },
    niveauFormation: optionnelPrestataireArray,
    numeroDossierCpf: (_value, _index, _parent, model, _context) => {
      return model.sousDispositif === 'AbondementCpf' ? PropertyStatus.Optional : PropertyStatus.NotPresent
    },
    tauxHoraireBrut: (_value, _index, _parent, model, _context) => {
      if (model.formation?.modules?.some((module) => {
        return module.fraisPedagogiques?.some((frais) => frais.montant)
      }) || (model.formation?.modules?.some((module) => {
        return module.fraisAnnexesSalarie?.some((frais) => frais.montant)
      }) || model.sousDispositif !== 'PDC')) return PropertyStatus.Optional
      return PropertyStatus.Required
    },
    pctTauxCharge: (_value, _index, parent, _model, context) => {
      if (context.typeContext === TypesContextesAutorisesEnum.Prestataire) return PropertyStatus.Optional

      return isNullOrUndefined(parent.tauxHoraireBrut) ? PropertyStatus.Optional : PropertyStatus.Required
    },
    eligibilite: (_value, _index, _parent, model, context) => {
      if (context.typeContext === TypesContextesAutorisesEnum.Prestataire) return PropertyStatus.NotPresent

      return model.sousDispositif === 'AfFpe' && model.referenceProjet === 'ParcoursAlternatifs'
        ? PropertyStatus.Required
        : PropertyStatus.NotPresent
    },
  },
  formation: {
    objectifFormation: (_value, _parent, model, _context) => {
      return model.sousDispositif === 'AfFpe' ? PropertyStatus.Optional : PropertyStatus.Required
    },
    referenceTypeProjet: (_value, _parent, _model, _context) => {
      return PropertyStatus.Optional
    },
    modules: {
      modalitePedagogique: PropertyStatus.Required,
      dureeFoad: (_value, _index, parent, _model, _context) => {
        return parent.modalitePedagogique === 'Mixte' ? PropertyStatus.Required : PropertyStatus.NotPresent
      },
      lieuFormation: {
        $model: (_value, parent, model) => {
          return parent.modalitePedagogique === 'Distance' ||
            parent.modalitePedagogique === 'Foad' ||
            model.sousDispositif === 'AfFpe'
            ? PropertyStatus.Optional
            : PropertyStatus.Required
        },
        voie: PropertyStatus.Required,
        codePostal: PropertyStatus.Required,
        commune: PropertyStatus.Required,
      },
      typeQualification: PropertyStatus.Required,
      formacode: {
        $model: (_value, parent) => {
          if (!parent.typeQualification) return PropertyStatus.NotPresent
          const nomenclatures = TypeQualificationNomenclatures[parent.typeQualification]
          if (nomenclatures.includes('formacode') === false) return PropertyStatus.NotPresent
          if (nomenclatures.length === 1 || parent.typeReferentielFormation === 'Formacode')
            return PropertyStatus.Required
          return PropertyStatus.NotPresent
        },
        code: PropertyStatus.Required,
        intitule: PropertyStatus.Required,
      },
      rncp: {
        $model: (_value, parent) => {
          if (!parent.typeQualification) return PropertyStatus.NotPresent
          const nomenclatures = TypeQualificationNomenclatures[parent.typeQualification]
          if (nomenclatures.includes('rncp') === false) return PropertyStatus.NotPresent
          if (nomenclatures.length === 1 || parent.typeReferentielFormation === 'Rncp') return PropertyStatus.Required
          return PropertyStatus.NotPresent
        },
        code: PropertyStatus.Required,
        intitule: PropertyStatus.Required,
      },
      repertoireSpecifique: {
        $model: (_value, parent) =>
          parent.typeQualification &&
          TypeQualificationNomenclatures[parent.typeQualification].includes('repertoireSpecifique')
            ? PropertyStatus.Required
            : PropertyStatus.NotPresent,
        code: PropertyStatus.Required,
        intitule: PropertyStatus.Required,
      },
      intitule: (...[, , parent]) =>
        parent.typeQualification === 'AttestationStage' ||
        (parent.typeQualification === 'Cqp' && parent.typeReferentielFormation === 'Formacode')
          ? PropertyStatus.Required
          : PropertyStatus.Optional,
      typeReferentielFormation: (...[, , parent]) =>
        parent.typeQualification === 'Cqp' ? PropertyStatus.Required : PropertyStatus.NotPresent,
      typeModule: PropertyStatus.Required,
      avecSubrogation: (_value, _index, parent, _model, context) => {
        return parent.typeModule === 'Interne' || context.typeContext === TypesContextesAutorisesEnum.Prestataire
          ? PropertyStatus.NotPresent
          : PropertyStatus.Required
      },
      dateDebut: PropertyStatus.Required,
      dateFin: PropertyStatus.Required,
      duree: PropertyStatus.Required,
      niveauCertificationVisee: PropertyStatus.Required,
      objectifPrestataire: (_value, _index, parent, model, _context) => {
        if (parent.typeModule === 'Interne') return PropertyStatus.NotPresent
        return model.sousDispositif === 'AfFpe' ? PropertyStatus.Optional : PropertyStatus.Required
      },
      typeFormation: (_value, _index, parent, _model, _context) => {
        return parent.typeModule === 'Interne' ? PropertyStatus.NotPresent : PropertyStatus.Required
      },
      fraisPedagogiques: {
        montant: (_value, _index, parent) => {
          return parent.nature === 'Pedagogique' ? PropertyStatus.Required : PropertyStatus.Optional
        },
      },
      etablissement: {
        $model: (_value, parent) =>
          parent.typeModule === 'Interne' ? PropertyStatus.NotPresent : PropertyStatus.Required,
      },
    },
  },
  financement: {
    $model: (_value, _parent, model, context) => {
      if (!estTT(model)) return PropertyStatus.NotPresent
      if (context.typeContext === TypesContextesAutorisesEnum.Prestataire) return PropertyStatus.Optional
      return PropertyStatus.Required
    },
    attestationResteChargeContribution: (_value, _index, _parent, context) => {
      if (context.typeContext !== TypesContextesAutorisesEnum.Adherent) return PropertyStatus.NotPresent
      return PropertyStatus.Required
    },
    modePriseEnChargeReste: (_value, _index, parent) => {
      if (parent.financement?.attestationResteChargeContribution === true) return PropertyStatus.Required
      return PropertyStatus.Optional
    },
  },
} as const
